/**
 * NewBuildToast — listens for 'build:updated' from build-version-poller.ts
 * and offers a one-tap reload. We never reload silently because a field
 * user could be mid-narrative; let them choose the moment.
 *
 * Once dismissed for a given commit, the toast stays dismissed for that
 * commit until something even newer arrives. Reload uses location.reload()
 * which forces the SW to fetch the new HTML and hashed JS bundle.
 */

import { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';

interface BuildUpdatedDetail {
  fromCommit: string;
  toCommit: string;
  builtAt?: string;
}

export function NewBuildToast() {
  const [pending, setPending] = useState<BuildUpdatedDetail | null>(null);
  const [dismissedCommit, setDismissedCommit] = useState<string | null>(null);

  useEffect(() => {
    const onUpdated = (e: Event) => {
      const detail = (e as CustomEvent<BuildUpdatedDetail>).detail;
      // If the user previously dismissed this exact commit, respect it.
      if (detail.toCommit === dismissedCommit) return;
      setPending(detail);
    };
    window.addEventListener('build:updated', onUpdated as EventListener);
    return () => window.removeEventListener('build:updated', onUpdated as EventListener);
  }, [dismissedCommit]);

  if (!pending) return null;

  const reload = () => {
    // Best effort: tell any active SW to skip waiting before reload.
    if ('serviceWorker' in navigator) {
      void navigator.serviceWorker.getRegistration().then((reg) => {
        reg?.waiting?.postMessage({ type: 'SKIP_WAITING' });
      });
    }
    // Hard reload — bypass HTTP cache. After this, the new SW serves
    // the new index.html which references the new hashed bundle.
    window.location.reload();
  };

  const dismiss = () => {
    setDismissedCommit(pending.toCommit);
    setPending(null);
  };

  return (
    <div
      role="status"
      className="fixed top-4 right-4 z-50 max-w-sm rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 shadow-lg flex items-start gap-3"
    >
      <RefreshCw size={20} className="text-emerald-700 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-emerald-900">
          New version available
        </div>
        <div className="text-xs text-emerald-800 mt-0.5">
          Reload to get the latest build. Your in-progress work syncs first.
        </div>
        <div className="flex gap-2 mt-2">
          <button
            onClick={reload}
            className="rounded-md border border-emerald-700 bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-800"
          >
            Reload now
          </button>
          <button
            onClick={dismiss}
            className="rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"
          >
            Later
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded p-1 text-emerald-700 hover:bg-emerald-100"
      >
        <X size={16} />
      </button>
    </div>
  );
}
